import { z } from 'zod';

import { ERROR_TYPE } from '../consts';
import type { ApiError, NetworkError, SchemaValidationError, ApiResponseError, UnknownError } from '../types';
import { ForbiddenErrorSchema, type ForbiddenError, RateLimitedErrorSchema, type RateLimitedError } from './legacy';
import type { ErrorEnvelopeBody } from './schemas';

// Guards for error types
export const isNetworkError = (error: ApiError): error is NetworkError => error.type === ERROR_TYPE.NETWORK;

export const isSchemaValidationError = (error: ApiError): error is SchemaValidationError =>
  error.type === ERROR_TYPE.SCHEMA_VALIDATION;

export const isApiResponseError = (error: ApiError): error is ApiResponseError<unknown> =>
  error.type === ERROR_TYPE.API_RESPONSE;

export const isUnknownError = (error: ApiError): error is UnknownError => error.type === ERROR_TYPE.UNKNOWN;

const CLIENT_ERROR_MIN_STATUS = 400;
const CLIENT_ERROR_MAX_STATUS = 499;

/**
 * True for an `ApiError` carrying a 4xx response. The request was understood and answered,
 * so retrying it cannot change the outcome — retry policies skip these and only retry
 * network / 5xx failures.
 */
export const isHttpClientError = (error: unknown) =>
  isApiError(error) &&
  error.status !== undefined &&
  error.status >= CLIENT_ERROR_MIN_STATUS &&
  error.status <= CLIENT_ERROR_MAX_STATUS;

// Guard for 403 Forbidden error payload shape
export const isForbiddenError = (error: unknown): error is ForbiddenError =>
  ForbiddenErrorSchema.safeParse(error).success;

// The two fields every unwrapped Manage API error envelope has
const ErrorEnvelopeBodyShapeSchema = z.object({ code: z.string(), message: z.string() });

// Guard for the unwrapped Manage API error envelope carried by an `ApiResponseError`
export const isErrorEnvelopeBody = (error: unknown): error is ErrorEnvelopeBody =>
  ErrorEnvelopeBodyShapeSchema.safeParse(error).success;

// Guard for 429 Rate Limited error payload shape
export const isRateLimitedError = (error: unknown): error is RateLimitedError =>
  RateLimitedErrorSchema.safeParse(error).success;

// Guard for AbortError
// Detects both Error and DOMException with name 'AbortError'.
export const isAbortError = (error: unknown) =>
  (error instanceof Error && error.name === 'AbortError') ||
  (error instanceof DOMException && error.name === 'AbortError');

const API_ERROR_TYPES = new Set<unknown>(Object.values(ERROR_TYPE));

// Guard for unknown → ApiError — discriminates on `type` against the known ERROR_TYPE
// values so every ApiError variant is accepted, including a status-less UnknownError.
export const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' && error !== null && 'type' in error && API_ERROR_TYPES.has(error.type);
